import type { IProductBundleOption } from "@rogue/universal"
import type { BundleUIOption } from "./RuleTypes"
import { shouldHideIndyTrolleyAttachment } from "./rules/indy-rules"
import { getOptionsForLatAndLowRow } from "./rules/lat-low-row-rules"
import { getOptionsForMutantMetalsBundle } from "./rules/mutant-metals-rules"
import { getOptionsForTrapBarBundle } from "./rules/trap-bar-rules"
import { getOptionsForRackBundle } from "./rules/rack-rules"
import { getOptionsForHybridRig } from "./rules/hybrid-rules"
import { getRackOptionsForTheCave } from "./rules/cave-rules"
import { getOptionsForSlingerX } from "./rules/slingerX-rules"
import { getSimpleForBuyRequest } from "@/js/helpers/buy-request"
import type { BuyRequest, IRogue, Product } from '@/js/services/RogueTypes'
import { findBundleProduct } from "@/js/pages/product/rackbuilder/utils/rackbuilder-util"
import { isEnabledCustomProduct, isEnabledCustomProductMember } from '@/js/customProducts/registry'
import { getOptionsForMassStorage } from "./rules/mass-storage-rules"
import { MSUnit } from '@/js/pages/product/rackbuilder/utils/ms-utils/ms-constants'
import { MMSUnit } from "@/js/pages/product/rackbuilder/utils/mms-utils/mms-constants"
import { TRAP_BAR_BUNDLE_SKU } from "@/js/stores/productConstants"


// Return a "name" for the product, that allows us to compare how similar other product options are
function getNameForCompare(product: Product) {
  if (!product) {
    return []
  }

  // Add the price to the name, so that products that are the same price are more likely to select
  let name = `${product.name} ${product.price}`.replace(/^[a-zA-Z0-9 ]/g, '')

  // A 43" Top Crossmember - Satin Black should turn into 43" Top Crossmember beam_color, so that it compares similarly to 43" Top Crossmember Bright Blue
  const attrsToReplace = ['beam_color', 'color']
  attrsToReplace.forEach(attr => name = name.replace(`${product.attributes[attr]}`, attr))

  // We tokenize it so we can compare the number of similar symbols
  return name.split(' ')
}

// If we select a new option that invalidates our previous bundle option, rather than reset to the default option, this function lets us try to find the most
// similar still-valid option. For example if we change from 30" depth to 43" depth in the rack, we'd rather keep the same safety type selected, even if the depth changed.
export function findMostSimilarOption(rogue:IRogue, currentProductId: number, availableOptions: BundleUIOption[]) {
  const currentProduct = rogue.getProductById(currentProductId)
  // We are going to compare the names of the products to see which one is most similar to the old product
  const currentName = getNameForCompare(currentProduct)

  const optionsByDistance = availableOptions.map(opt => {
    if (!opt.value) {
      return { value: opt.value, sharedWords: 0 }
    }

    const product = rogue.getProductById(opt.value)
    const newName = getNameForCompare(product)

    // We are going to pick which one is longer, so we always have a denominator of the largest value
    const longArray = currentName.length > newName.length ? currentName : newName
    const shortArray = currentName.length <= newName.length ? currentName : newName

    // See how many words the titles share
    const sharedWords = longArray.filter(w => shortArray.includes(w)).length / longArray.length

    return { value: opt.value, sharedWords }
  })

  optionsByDistance.sort((a, b) => b.sharedWords - a.sharedWords)

  return optionsByDistance[0].value
}

function getOptionsForConfigurableMainProduct(rogue: IRogue, configBuyRequest: BuyRequest, options: BundleUIOption[]) {
  const config = rogue.getProductById(configBuyRequest.productId)
  const selectedSimple = getSimpleForBuyRequest(configBuyRequest, rogue)


  return options.filter(opt => {
    // Always show None if its an option
    if (!opt.value) {
      return true
    }

    const product = rogue.getProductById(opt.value)
    const sideAttribute = String(selectedSimple.attributes?.side).toLowerCase()
    const isIndy = sideAttribute === 'dual' || sideAttribute === 'left' || sideAttribute === 'right'
    const isRhinoRackTrainer = selectedSimple.attributes.rig_type?.toString().includes('Rhino Rack Trainer') 
    const isRhinoFXT = selectedSimple.attributes.rig_type?.toString().includes('Rhino Functional Trainer') 
    const isSingleTrolleyAttachment = product.name.toLowerCase()?.includes('single handle cable attachment')
    const isHyroxPackages = config.name.toLocaleLowerCase()?.includes('hyrox packages')

    const isTheCavePanel = selectedSimple.name.toLowerCase().includes('cave') 
    && selectedSimple.name.toLowerCase().includes('2.0') 
    && product.name.toLowerCase().includes('panel')

    if (isTheCavePanel) {
      const massWidth = String(selectedSimple?.attributes?.mass_width)
      const is70Width = massWidth.includes('70')
      const is43Width = massWidth.includes('43')
      const is30Width = massWidth.includes('30')
  
      const isMismatchedPanelWidth = (is43Width && product.name.includes('30')) || (is30Width && product.name.includes('43'))

      // Hide storage panels on 70" width cave sides, or mismatched panel widths
      if (is70Width || isMismatchedPanelWidth) {
        return false
      }
    }

    // Hide trolley attachments for Indy & Rhino+INDY addons
    if (isIndy && shouldHideIndyTrolleyAttachment(isSingleTrolleyAttachment, sideAttribute, product.name)) {
      return false
    }

    // Hide the trolley attachment on the Rhino Rack Trainer Add-on
    if (isRhinoRackTrainer && product.name.includes('Single Handle Cable Attachment')) {
      return false
    }

    // Hide the non dual single trolley attachment on the Rhino FXT Add-on
    if (isRhinoFXT && isSingleTrolleyAttachment && !String(product.name).toLowerCase()?.includes('(pair)')) {
      return false
    }

    // If we don't know about the product, this is an invalid bundle option
    if (!product) {
      return false
    }

    // Configs are never hidden. Maybe this could check every child to see if any of them are a possibility?
    if (product.type !== 'simple') {
      return true
    }

    // Echo Dog Sled `edition` attribute is unrelated to Division and reused for its standalone page, so it can't be cleared in Magento — skip the check below
    if (isHyroxPackages) {
      return true
    }

    // Make sure that the selected simple either doesn't have the attributes of the configurable, or has them and they match
    return !config.configurableAttributes?.some(attrToCheck => product.attributes[attrToCheck.code] && product.attributes[attrToCheck.code] !== selectedSimple.attributes[attrToCheck.code])
  })
}

export function getBundleOptions(rogue: IRogue, bundleOption: IProductBundleOption, bundleBuyRequest: BuyRequest, options:BundleUIOption[], rackBuyRequestOverride?: BuyRequest) {
  const rackBundleProduct = findBundleProduct(rogue, bundleBuyRequest, 'rack')
  const productName = rackBundleProduct?.name?.toLowerCase()
  const isLatAndLowRow = productName?.includes('lat pull low row trainer') 
  
  const bundleProduct = rogue.getProductById(bundleBuyRequest?.productId)
  
  const isMassStorage = MSUnit.includes(bundleProduct?.sku) || MMSUnit.includes(bundleProduct?.sku)

  const isTheCave = isEnabledCustomProduct(rogue, 'cave', bundleProduct)
  const isSlingerX = isEnabledCustomProductMember(rogue, 'slinger-x', 'mainSkus', bundleProduct?.sku)
  const isHybridRig = isEnabledCustomProduct(rogue, 'hybrid-rig', bundleProduct)
  const isArcBundle = bundleProduct?.sku?.includes('MM-ARC-BUNDLE')
  const isVGripBundle = bundleProduct?.sku?.includes('MM-V-GRIP-BUNDLE')
  const isTrapBarBundle = bundleProduct?.sku?.includes(TRAP_BAR_BUNDLE_SKU)

  // We are going to incrementally filter out options that are not viable
  let retval = options
  if (isHybridRig && rackBuyRequestOverride) {
    const hybridRackProduct = getSimpleForBuyRequest(rackBuyRequestOverride, rogue)
    retval = getOptionsForHybridRig(rogue, hybridRackProduct, bundleOption, options, bundleBuyRequest) || []
  } else if (isTheCave && rackBundleProduct) {
    retval = getRackOptionsForTheCave(rogue, rackBundleProduct, bundleOption, options, bundleBuyRequest) || []
  } else if (isMassStorage && rackBundleProduct) {
    const isMonsterMassStorage = MMSUnit.includes(bundleProduct?.sku)
    retval = getOptionsForMassStorage(rogue, rackBundleProduct, bundleOption, options, bundleBuyRequest, isMonsterMassStorage) || []
  } else if (isLatAndLowRow && rackBundleProduct) {
    retval = getOptionsForLatAndLowRow(rogue, rackBundleProduct, bundleOption, options, bundleBuyRequest) || []
  } else if (isSlingerX && rackBundleProduct) {
    retval = getOptionsForSlingerX(rogue, rackBundleProduct, bundleOption, options, bundleBuyRequest) || []
  } else if (rackBundleProduct) {
    // We hide or modify options if its in a bundle of a functional trainer.
    retval = getOptionsForRackBundle(rogue, rackBundleProduct, bundleOption, options, bundleBuyRequest) || []
  } else if (isArcBundle || isVGripBundle) {
    retval = getOptionsForMutantMetalsBundle(rogue, bundleProduct, bundleOption, options, bundleBuyRequest) || []
  } else if (isTrapBarBundle) {
    retval = getOptionsForTrapBarBundle(rogue, bundleOption, options, bundleBuyRequest)
  } else {
    // If the first product is a configurable, and the children of this bundle option have that value, remove the ones that don't match.
    const firstProduct = bundleBuyRequest.children?.[0]

    if (firstProduct?.children?.[0]) {
      retval = getOptionsForConfigurableMainProduct(rogue, firstProduct, options)
    }
  }

  // If not every option has an image, we need to remove images from the rest of them
  const removeImages = retval.some(opt => !opt.image) && retval.some(opt => opt.image)
  retval = retval
    .filter(opt => !opt.value || Boolean(rogue.getProductById(opt.value)))
    .map(opt => (removeImages ? { ...opt, image: '' } : opt))

  return retval
}

